/**
 * ENGAGE-ADDON-PRICING-1 step 1/2/3: add-on selection + per-item price
 * override state and derived values, extracted out of EngagementCreate.tsx
 * to keep that file under the FILE_SIZE_POLICY 400-line hard stop (adding
 * the override UI pushed it back over, the same reason TierFeeCard and
 * AddOnsCard were split out under ENGAGE-PRICING-SPLIT-1).
 *
 * Depends only on effectiveTier/baseFee, both owned by the caller — this
 * hook has no opinion on how the tier or server quote were derived.
 */
import { useMemo, useState } from "react";
import { ADD_ONS, addOnBaseAmount, tierAtLeast } from "@/lib/engagementIntake";
import type { ScopeTier } from "@/lib/qoeCatalogue.generated";

export interface AddOnFinal {
  base: number;
  final: number;
  overridden: boolean;
}

export interface AddOnPayloadItem {
  key: string;
  label: string;
  base_amount: number;
  final_amount: number;
  overridden: boolean;
  override_reason: string | null;
}

export function useAddOnSelection(effectiveTier: ScopeTier, baseFee: number) {
  const [selectedAddOns, setSelectedAddOns] = useState<Set<string>>(new Set());
  // Per-add-on price override, keyed by ADD_ONS[].key. Empty/absent means
  // "no override" — same convention as tierOverride's "auto" sentinel in
  // EngagementCreate.tsx, just per-item instead of singular.
  const [addOnOverrideAmounts, setAddOnOverrideAmounts] = useState<Record<string, string>>({});
  const [addOnOverrideReasons, setAddOnOverrideReasons] = useState<Record<string, string>>({});

  function toggleAddOn(key: string) {
    setSelectedAddOns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  function setAddOnOverrideAmount(key: string, value: string) {
    setAddOnOverrideAmounts(prev => ({ ...prev, [key]: value }));
  }
  function setAddOnOverrideReason(key: string, value: string) {
    setAddOnOverrideReasons(prev => ({ ...prev, [key]: value }));
  }

  // One place that resolves "what is this deal actually being charged for
  // add-on X" — the catalogue amount unless a super_admin entered an
  // override, in which case the override wins. Feeds the running total, the
  // pre-submit reason gate, and the API payload below, so none of the three
  // can ever disagree with each other.
  const addOnFinalAmounts = useMemo(() => {
    const finals: Record<string, AddOnFinal> = {};
    for (const key of selectedAddOns) {
      const ao = ADD_ONS.find(a => a.key === key);
      if (!ao) continue;
      if (ao.minTier && !tierAtLeast(effectiveTier, ao.minTier)) continue;
      const base = addOnBaseAmount(ao, effectiveTier, baseFee);
      const overrideRaw = addOnOverrideAmounts[key] ?? "";
      const overrideParsed = overrideRaw.trim() === "" ? null : parseFloat(overrideRaw);
      const overridden = overrideParsed !== null && !isNaN(overrideParsed) && overrideParsed !== base;
      finals[key] = { base, final: overridden ? (overrideParsed as number) : base, overridden };
    }
    return finals;
  }, [selectedAddOns, baseFee, effectiveTier, addOnOverrideAmounts]);

  const addOnTotal = useMemo(
    () => Object.values(addOnFinalAmounts).reduce((sum, v) => sum + v.final, 0),
    [addOnFinalAmounts],
  );

  // Returns the label of the first overridden add-on with no reason, or null
  // once every override has one. The server enforces this too (crud.py) —
  // this just avoids a round trip for the common typo.
  function findOverrideMissingReason(): string | null {
    for (const [key, v] of Object.entries(addOnFinalAmounts)) {
      if (v.overridden && !(addOnOverrideReasons[key] ?? "").trim()) {
        const ao = ADD_ONS.find(a => a.key === key);
        return ao?.label ?? key;
      }
    }
    return null;
  }

  // What POST /qoe/deals stores against the deal — see selected_add_ons on
  // Deal (core/db/models/qoe.py) for the shape this mirrors.
  function buildPayload(): AddOnPayloadItem[] {
    return Object.entries(addOnFinalAmounts).map(([key, v]) => {
      const ao = ADD_ONS.find(a => a.key === key);
      return {
        key,
        label: ao?.label ?? key,
        base_amount: v.base,
        final_amount: v.final,
        overridden: v.overridden,
        override_reason: v.overridden ? (addOnOverrideReasons[key] ?? "").trim() : null,
      };
    });
  }

  return {
    selectedAddOns, toggleAddOn,
    addOnOverrideAmounts, setAddOnOverrideAmount,
    addOnOverrideReasons, setAddOnOverrideReason,
    addOnTotal,
    findOverrideMissingReason, buildPayload,
  };
}
